import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Progress } from '@/components/ui/progress';
import { ChevronLeft, ChevronRight, ArrowUpDown, Building2, Package, Info } from 'lucide-react';
import { LowStockItemEnhanced } from '@/app/types/lowStockReport';
import { cn } from '@/lib/utils';

interface LowStockDataTableProps {
  data: LowStockItemEnhanced[];
  total: number;
  selectedItems: LowStockItemEnhanced[];
  onSelectionChange: (items: LowStockItemEnhanced[]) => void;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onSort: (column: string, direction: 'asc' | 'desc') => void;
}

export const LowStockDataTable: React.FC<LowStockDataTableProps> = ({
  data,
  total,
  selectedItems,
  onSelectionChange,
  currentPage,
  pageSize,
  onPageChange,
  onSort,
}) => {
  const totalPages = Math.ceil(total / pageSize);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedItems, ...data.filter(item => 
        !selectedItems.some(selected => selected.item_id === item.item_id && selected.godown_id === item.godown_id)
      )]);
    } else {
      onSelectionChange(selectedItems.filter(item =>
        !data.some(dataItem => dataItem.item_id === item.item_id && dataItem.godown_id === item.godown_id)
      ));
    }
  };

  const handleSelectItem = (item: LowStockItemEnhanced, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedItems, item]);
    } else {
      onSelectionChange(selectedItems.filter(selected => 
        !(selected.item_id === item.item_id && selected.godown_id === item.godown_id)
      ));
    }
  };

  const isItemSelected = (item: LowStockItemEnhanced) => {
    return selectedItems.some(selected => 
      selected.item_id === item.item_id && selected.godown_id === item.godown_id
    );
  };

  const getActionBadge = (action: string) => {
    switch (action) {
      case 'transfer_available':
        return <Badge variant="default" className="bg-green-500">Transfer Available</Badge>;
      case 'partial_transfer':
        return <Badge variant="secondary" className="bg-yellow-500">Partial Transfer</Badge>;
      case 'purchase_required':
        return <Badge variant="destructive">Purchase Required</Badge>;
      default:
        return null;
    }
  };

  const getSeverityColor = (shortage: string | null, reorderLevel: string | null) => {
    if (!shortage || !reorderLevel) return 'text-gray-500';
    const ratio = parseFloat(shortage) / parseFloat(reorderLevel);
    if (ratio > 0.8) return 'text-red-600 font-semibold';
    if (ratio > 0.5) return 'text-orange-600';
    if (ratio > 0.3) return 'text-yellow-600';
    return 'text-gray-600';
  };

  const formatQuantity = (value: string | null) => {
    if (!value) return '0';
    const num = parseFloat(value);
    return num % 1 === 0 ? num.toString() : num.toFixed(2);
  };

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]">
                <Checkbox
                  checked={data.length > 0 && data.every(item => isItemSelected(item))}
                  onCheckedChange={handleSelectAll}
                />
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  className="h-8 p-0"
                  onClick={() => onSort('item_name', 'asc')}
                >
                  Item
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead>Location</TableHead>
              <TableHead className="text-right">
                <Button
                  variant="ghost"
                  className="h-8 p-0"
                  onClick={() => onSort('current_quantity', 'desc')}
                >
                  Current Stock
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead className="text-right">Reorder Level</TableHead>
              <TableHead className="text-right">
                <Button
                  variant="ghost"
                  className="h-8 p-0"
                  onClick={() => onSort('shortage', 'desc')}
                >
                  Shortage
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead className="text-center">Stock Distribution</TableHead>
              <TableHead>Action Suggested</TableHead>
              <TableHead className="text-right">Available for Transfer</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((item) => (
              <TableRow key={`${item.item_id}-${item.godown_id}`}>
                <TableCell>
                  <Checkbox
                    checked={isItemSelected(item)}
                    onCheckedChange={(checked) => handleSelectItem(item, !!checked)}
                  />
                </TableCell>
                <TableCell>
                  <div>
                    <div className="font-medium">{item.item_name}</div>
                    {item.item_code && (
                      <div className="text-sm text-muted-foreground">{item.item_code}</div>
                    )}
                    <div className="text-sm text-muted-foreground">{item.unit_name}</div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="text-sm font-medium">{item.godown_name || 'N/A'}</div>
                      <div className="text-xs text-muted-foreground">{item.current_site_name || 'N/A'}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <span className={cn(
                    parseFloat(item.current_quantity) <= 0 && 'text-red-600 font-semibold'
                  )}>
                    {formatQuantity(item.current_quantity)}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  {formatQuantity(item.reorder_level)}
                </TableCell>
                <TableCell className="text-right">
                  <span className={getSeverityColor(item.shortage, item.reorder_level)}>
                    {formatQuantity(item.shortage)}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 space-y-1">
                      {item.site_stock_details.slice(0, 2).map((site) => (
                        <div key={site.site_id} className="flex items-center gap-2 text-xs">
                          <div className="w-16 truncate">{site.site_name}</div>
                          <Progress 
                            value={
                              site.reorder_level 
                                ? (parseFloat(site.available_quantity) / parseFloat(site.reorder_level)) * 100
                                : 0
                            }
                            className="h-2 flex-1"
                          />
                          <span className={cn(
                            'min-w-[40px] text-right',
                            site.is_low_stock ? 'text-red-600' : 'text-green-600'
                          )}>
                            {formatQuantity(site.available_quantity)}
                          </span>
                        </div>
                      ))}
                      {item.site_stock_details.length > 2 && (
                        <div className="text-xs text-muted-foreground">
                          +{item.site_stock_details.length - 2} more
                        </div>
                      )}
                    </div>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 hover:bg-slate-100"
                        >
                          <Info className="h-3 w-3" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 p-0 overflow-hidden">
                          <div className="bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950">
                            <div className="px-4 py-3 border-b bg-slate-100 dark:bg-slate-800">
                              <h4 className="font-semibold text-sm">Stock Distribution</h4>
                            </div>
                            <div className="p-4 space-y-3">
                              {item.site_stock_details.map((site) => (
                                <div key={site.site_id} className="space-y-1">
                                  <div className="flex items-center justify-between text-sm">
                                    <span className="font-medium text-slate-700 dark:text-slate-300">
                                      {site.site_name}
                                    </span>
                                    <div className="flex items-center gap-2">
                                      <span className={cn(
                                        "font-semibold",
                                        site.is_low_stock ? 'text-red-600' : 'text-slate-900 dark:text-slate-100'
                                      )}>
                                        {formatQuantity(site.available_quantity)}
                                      </span>
                                      <span className="text-slate-400">/</span>
                                      <span className="text-slate-500 text-xs">
                                        {formatQuantity(site.reorder_level)}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <div className="flex-1 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                      <div 
                                        className={cn(
                                          "h-full transition-all",
                                          site.is_low_stock 
                                            ? "bg-gradient-to-r from-red-500 to-red-600" 
                                            : "bg-gradient-to-r from-green-500 to-green-600"
                                        )}
                                        style={{
                                          width: `${Math.min(
                                            site.reorder_level 
                                              ? (parseFloat(site.available_quantity) / parseFloat(site.reorder_level)) * 100
                                              : 0,
                                            100
                                          )}%`
                                        }}
                                      />
                                    </div>
                                    {site.excess_quantity && parseFloat(site.excess_quantity) > 0 && (
                                      <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                        +{formatQuantity(site.excess_quantity)}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              ))}
                              <div className="pt-3 mt-3 border-t border-slate-200 dark:border-slate-700">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                                    Total Organization Stock
                                  </span>
                                  <span className="text-lg font-bold text-slate-900 dark:text-slate-100">
                                    {formatQuantity(item.total_organization_stock)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </TableCell>
                <TableCell>{getActionBadge(item.suggested_action)}</TableCell>
                <TableCell className="text-right">
                  <span className={cn(
                    parseFloat(item.available_for_transfer) > 0 
                      ? 'text-green-600 font-medium' 
                      : 'text-gray-500'
                  )}>
                    {formatQuantity(item.available_for_transfer)}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, total)} of {total} items
          {selectedItems.length > 0 && (
            <span className="ml-4 font-medium text-foreground">
              {selectedItems.length} item(s) selected
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <div className="text-sm">
            Page {currentPage} of {totalPages}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};